import React, { useEffect, useState } from 'react';
import {
    Grid,
    Typography,
    TextField,
    Button,
    DialogContent,
    MenuItem,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import DraggableDialog from '../../../../ui/draggableDialog';
import { toast } from 'react-toastify';
import { isLocalDev } from 'utils/environment';
import { ContactLensConfigurationService, ValidDataService } from 'service/validDataService';
import { initialValuesModelForm } from './contactLensModelInputForm';
import useLensTypeCatalog from 'hooks/useLensTypeCatalog';

const validationSchema = Yup.object({
    manufacturerId: Yup.number().required('Manufacturer is required').typeError('Manufacturer is required'),
    modelName: Yup.string().required('Model name is required'),
    lensType: Yup.string().required('Lens type is required'),
        trialQuantity: Yup.number().nullable().min(0, 'Trial quantity must be >= 0')
});

export default function EditContactLensModelForm({ open, onClose, onRecordEdit, recordToEdit, isCopy = false }) {
    const { contactLensTypes } = useLensTypeCatalog();
    const [manufacturers, setManufacturers] = useState([]);
    const [materials, setMaterials] = useState([]);
    const [loadingData, setLoadingData] = useState(false);

    useEffect(() => {
        if (open) {
            loadData();
        }
    }, [open]);

    const loadData = async () => {
        setLoadingData(true);
        try {
            const [manufacturerResponse, materialResponse] = await Promise.all([
                ValidDataService.getManufacturersByType('contact_lens'),
                ContactLensConfigurationService.getActiveMaterials()
            ]);

            if (manufacturerResponse.code === 0) {
                setManufacturers(manufacturerResponse.data || []);
            }
            if (materialResponse.code === 0) {
                setMaterials(materialResponse.data || []);
            }
        } catch (error) {
            if (isLocalDev) {
                console.error(error);
            }
            toast.error('Failed to load manufacturers and materials');
        }
        setLoadingData(false);
    };

    const handleSubmit = async (values, { resetForm }) => {
        try {
            let response;

            if (isCopy) {
                const { id, ...newValues } = values;
                response = await ContactLensConfigurationService.addModel(newValues);
            } else {
                response = await ContactLensConfigurationService.updateModel(values);
            }

            const { code, data, message } = response;
            if (code === 0) {
                toast.success(message);
                onClose();
                onRecordEdit(data);
            } else {
                toast.error(message);
            }
        } catch (error) {
            if (isLocalDev) {
                console.log(error);
            }
            toast.error('Failed to update contact lens model');
        }
    };

    return (
        <DraggableDialog 
            title={isCopy ? "Copy Contact Lens Model" : "Edit Contact Lens Model"} 
            onClose={onClose} 
            open={open} 
            width={'sm'}
        >
            <DialogContent sx={{ mt: 1, mb: 1 }}>
                <Formik
                    initialValues={{ ...initialValuesModelForm, ...recordToEdit }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ isSubmitting, values }) => (
                        <Form>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="manufacturerId"
                                        label="Manufacturer"
                                        required
                                        fullWidth
                                        select
                                        size="small"
                                        disabled={loadingData}
                                    >
                                        <MenuItem value="">-- Select Manufacturer --</MenuItem>
                                        {manufacturers.map((mfg) => (
                                            <MenuItem key={mfg.id} value={mfg.id}>
                                                {mfg.name}
                                            </MenuItem>
                                        ))}
                                    </Field>
                                    <ErrorMessage name="manufacturerId">{message => <Typography color={'error'}>{message}</Typography>}</ErrorMessage>
                                </Grid>

                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="modelName"
                                        label="Model Name"
                                        required
                                        fullWidth
                                        size="small"
                                        helperText="e.g., Acuvue Oasys, Air Optix Aqua"
                                    />
                                    <ErrorMessage name="modelName">{message => <Typography color={'error'}>{message}</Typography>}</ErrorMessage>
                                </Grid>

                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="lensType"
                                        label="Lens Type"
                                        required
                                        fullWidth
                                        select
                                        size="small"
                                    >
                                        <MenuItem value="">-- Select Lens Type --</MenuItem>
                                        {contactLensTypes.map((type) => (
                                            <MenuItem key={type.key} value={type.key}>{type.label}</MenuItem>
                                        ))}
                                    </Field>
                                    <ErrorMessage name="lensType">{message => <Typography color={'error'}>{message}</Typography>}</ErrorMessage>
                                </Grid>

                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="materialId"
                                        label="Material"
                                        fullWidth
                                        select
                                        size="small"
                                        disabled={loadingData}
                                    >
                                        <MenuItem value="">-- Select Material (Optional) --</MenuItem>
                                        {materials.map((mat) => (
                                            <MenuItem key={mat.id} value={mat.id}>
                                                {mat.name} ({mat.waterContent}% water, Dk {mat.oxygenPermeability})
                                            </MenuItem>
                                        ))}
                                    </Field>
                                </Grid>

                                {/* <Grid item xs={12}>
                                    <Field
                                        as={FormControlLabel}
                                        control={<Checkbox name="uvProtection" />}
                                        label="UV Protection"
                                        name="uvProtection"
                                    />
                                </Grid> */}

                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name='status'
                                        label="Status"
                                        fullWidth
                                        select
                                        size="small"
                                    >
                                        <MenuItem value={"active"}>Active</MenuItem>
                                        <MenuItem value={"inactive"}>Inactive</MenuItem>
                                    </Field>
                                </Grid>

                                <Grid item xs={12}>
                                    <Field name="isTrialAvailable">
                                        {({ field, form }) => (
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        {...field}
                                                        checked={!!field.value}
                                                        onChange={(e) => form.setFieldValue(field.name, e.target.checked)}
                                                        color="primary"
                                                    />
                                                }
                                                label="Is Trial"
                                            />
                                        )}
                                    </Field>
                                </Grid>

                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="trialQuantity"
                                        label="Trial Quantity"
                                        type="number"
                                        size="small"
                                        fullWidth
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        disabled={isSubmitting || loadingData}
                                    >
                                        {isSubmitting ? (isCopy ? 'Copying...' : 'Updating...') : (isCopy ? 'Copy Model' : 'Update Model')}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>
            </DialogContent>
        </DraggableDialog>
    );
}
